import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  Container,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  CheckCircle,
  Error,
  ArrowBack,
  LocalShipping,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';

export const InvSupplierAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [id]);

  const fetchAnalytics = async () => {
    try {
      const response = await suppliersAPI.getAnalytics(id);
      setAnalytics(response.data.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ fontSize: '1.1rem', py: 2 }}>
          Analytics data not available for this supplier
        </Alert>
      </Container>
    );
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Back button and header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 6 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
          variant="outlined"
          sx={{
            px: 3,
            py: 1,
            borderRadius: 3,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Back to Suppliers
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AnalyticsIcon sx={{ fontSize: 42, color: 'primary.main' }} />
          <Box>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Supplier Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
              Performance metrics and insights
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha('#1976d2', 0.1)} 0%, ${alpha('#1976d2', 0.05)} 100%)`,
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <TrendingUp sx={{ fontSize: 52, color: 'primary.main', mb: 2 }} />
              <Typography variant="h2" fontWeight="bold" color="primary.main" gutterBottom>
                {analytics.fulfillmentRate}%
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Order Fulfillment Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha('#0288d1', 0.1)} 0%, ${alpha('#0288d1', 0.05)} 100%)`,
              border: '1px solid',
              borderColor: 'info.100',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Schedule sx={{ fontSize: 52, color: 'info.main', mb: 2 }} />
              <Typography variant="h2" fontWeight="bold" color="info.main" gutterBottom>
                {analytics.onTimeDelivery}%
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                On-Time Delivery
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha('#2e7d32', 0.1)} 0%, ${alpha('#2e7d32', 0.05)} 100%)`,
              border: '1px solid',
              borderColor: 'success.100',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 52, color: 'success.main', mb: 2 }} />
              <Typography variant="h2" fontWeight="bold" color="success.main" gutterBottom>
                {analytics.qualityScore}%
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Quality Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha('#ed6c02', 0.1)} 0%, ${alpha('#ed6c02', 0.05)} 100%)`,
              border: '1px solid',
              borderColor: 'warning.100',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <LocalShipping sx={{ fontSize: 52, color: 'warning.main', mb: 2 }} />
              <Typography variant="h2" fontWeight="bold" color="warning.main" gutterBottom>
                {analytics.avgLeadTime}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Avg Lead Time (Days)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Trends */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              borderRadius: 4,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.5rem' }}>
                📈 Monthly Performance
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Orders</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Fulfillment Rate</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>On-Time Delivery</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.monthlyPerformance?.map((month) => (
                      <TableRow 
                        key={month.month}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1rem' }}>
                            {month.month}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {month.orders}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Chip
                            label={`${month.fulfillmentRate}%`}
                            size="medium"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              minWidth: 80
                            }}
                            color={month.fulfillmentRate >= 95 ? 'success' : month.fulfillmentRate >= 85 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Chip
                            label={`${month.onTimeDelivery}%`}
                            size="medium"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              minWidth: 80
                            }}
                            color={month.onTimeDelivery >= 90 ? 'success' : month.onTimeDelivery >= 80 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Metrics */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              borderRadius: 4,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.5rem' }}>
                🎯 Quality Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Defect Rate
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                        {analytics.defectRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={100 - analytics.defectRate}
                      color={analytics.defectRate < 2 ? 'success' : analytics.defectRate < 5 ? 'warning' : 'error'}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 6,
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Return Rate
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                        {analytics.returnRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={100 - analytics.returnRate}
                      color={analytics.returnRate < 1 ? 'success' : analytics.returnRate < 3 ? 'warning' : 'error'}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 6,
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Compliance Score
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                        {analytics.complianceScore}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analytics.complianceScore}
                      color={analytics.complianceScore >= 95 ? 'success' : analytics.complianceScore >= 85 ? 'warning' : 'error'}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 6,
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Issues */}
        {analytics.recentIssues && analytics.recentIssues.length > 0 && (
          <Grid item xs={12}>
            <Card 
              sx={{ 
                borderRadius: 4,
                boxShadow: 2,
                '&:hover': { boxShadow: 4 }
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.5rem' }}>
                  ⚠️ Recent Issues
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Issue Type</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Order Number</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Status</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 2 }}>Severity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.recentIssues.map((issue) => (
                        <TableRow 
                          key={issue.id}
                          sx={{ 
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                              {new Date(issue.date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1rem' }}>
                              {issue.type}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                              {issue.orderNumber}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={issue.status}
                              size="medium"
                              sx={{ 
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                minWidth: 100
                              }}
                              color={issue.status === 'Resolved' ? 'success' : issue.status === 'In Progress' ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={issue.severity}
                              size="medium"
                              sx={{ 
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                minWidth: 90
                              }}
                              color={issue.severity === 'High' ? 'error' : issue.severity === 'Medium' ? 'warning' : 'info'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
    </Box>
  );
};